import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import isEmpty from 'lodash/isEmpty'

import { MAX_NUM_OF_QUESTION_OPTIONS } from 'cat-shared/utils'
import { CreateQuestionFormValues } from 'client/utils'
import { AppButton, AppDivider, AppForm, AppGroup } from 'client/ui-building-blocks'
import { fixScrollPosition } from 'client/helpers'
import { usePage } from 'client/hooks'
import { QuestionCategorySelect } from 'client/components'

import TypeSelect from './fields/type-select'
import TextInput from './fields/text-input'
import NumOfOptionsSlider from './fields/num-of-options-slider'
import OptionInputs from './fields/option-inputs'
import CorrectOptionsAutocomplete from './fields/correct-options-autocomplete'
import ExplanationTextArea from './fields/explanation-text-area'

import { type View } from '../create-question-page'

type Props = {
  isCreatingQuestion: boolean
  setActiveView: (view: View) => void
}

const NewQuestionForm = ({ isCreatingQuestion, setActiveView }: Props) => {
  const { t } = useTranslation()

  const { navigateTo } = usePage()

  const {
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useFormContext<CreateQuestionFormValues>()

  const onCancelClick = () => {
    navigateTo({ path: '/home' })
  }

  const onResetBtnClick = e => {
    e.preventDefault()
    fixScrollPosition(false)
    reset()
  }

  const onPreviewClick = () => {
    handleSubmit(() => {
      setActiveView('PREVIEW')
      fixScrollPosition(false)
    })()
  }

  return useMemo(() => {
    return (
      <AppForm>
        <QuestionCategorySelect isLoading={isCreatingQuestion} />
        <TextInput isLoading={isCreatingQuestion} />
        <AppDivider size="medium" border />
        <NumOfOptionsSlider step={1} min={2} max={MAX_NUM_OF_QUESTION_OPTIONS} />
        <OptionInputs isLoading={isCreatingQuestion} defaultValue={2} />
        <AppDivider size="medium" border />
        <TypeSelect />
        <CorrectOptionsAutocomplete isLoading={isCreatingQuestion} />
        <ExplanationTextArea isLoading={isCreatingQuestion} />
        <AppDivider size="medium" border />
        <AppGroup fullWidth="mobile" position="right">
          <AppButton
            variant="outlined"
            text={t('common.buttons.cancel')}
            onClick={onCancelClick}
            disabled={isCreatingQuestion}
          />
          <AppButton
            variant="blank"
            type="reset"
            text={t('common.buttons.reset')}
            onClick={onResetBtnClick}
            disabled={isCreatingQuestion || (!isDirty && isEmpty(errors))}
          />
          <AppButton
            variant="success"
            text={t('common.buttons.preview')}
            onClick={onPreviewClick}
            iconName="CHEVRON_RIGHT"
            iconPosition="right"
            disabled={isCreatingQuestion}
          />
        </AppGroup>
      </AppForm>
    )
  }, [isCreatingQuestion, isDirty, isEmpty(errors)])
}

export default NewQuestionForm
